import { uiEffectsEnabled } from "../graphics/graphics_quality.js";
import { Vector2 } from "../common/vector2.js";
import { Easing, TransformBase, TransformType } from "./transform_base.js";
import { TransformOpacity } from "./transform_opacity.js";

export enum BoardStage
{
  InitialAppear,
  NormalGame
}

export abstract class Transformable
{
  private pixel_pos:Vector2;
  private pixel_pos_addon:Vector2;
  private pixel_pos_for_render:Vector2;
  private scale:Vector2;
  private scale_addon:Vector2;
  private scale_for_render:Vector2;
  private opacity: number;
  private opacity_addon: number;
  private opacity_for_render: number;

  private rotation: number = 0;
  private rotation_addon: number = 0;
  private rotation_for_render: number = 0;

  private cur_transforms:TransformBase[] = []; // only one 'important' transformation of the same type should be active at a time

  private should_be_rendered: boolean = true; // maybeTODO: remove this (use is_animating instead)
  private explicit_dirty: boolean = false;
  protected dirty_on_set: boolean = false;
  private is_animating:boolean = false;
  private has_important_animation:boolean = false;
  private dom_opacity_driven: boolean = false;
  private dom_scale_driven: boolean = false;

  constructor( pixel_pos:Vector2, scale:Vector2|null = null, opacity:number = 1.0 )
  {
    this.pixel_pos = pixel_pos;
    this.scale = scale ?? new Vector2( 1, 1 );
    this.opacity = opacity;

    this.pixel_pos_addon = new Vector2( 0, 0 );
    this.scale_addon     = new Vector2( 0, 0 );
    this.opacity_addon   = 1.0;

    this.pixel_pos_for_render = this.pixel_pos.clone();
    this.scale_for_render     = this.scale.clone();
    this.opacity_for_render   = this.opacity;

    this.rotation_for_render = this.rotation;
  }

  public toStrTransformable():string
  {
    return `[pos ${this.pixel_pos} + ${this.pixel_pos_addon} = ${this.pixel_pos_for_render},  scale ${this.scale} * ${this.scale_addon} = ${this.scale_for_render}]`;
  }

  public logTransformsState()
  {
    if ( this.cur_transforms.length === 0 )
    {
      console.log( 'logTransformsState()   cur_transforms.length === 0' );
      return;
    }

    console.log( `logTransformsState()   this.cur_transforms.length ${this.cur_transforms.length}` );
    for ( let i = 0; i < this.cur_transforms.length; ++i )
    {
      const tr:TransformBase = this.cur_transforms[i];
      console.log( `   this.cur_transforms[${i}]   ${tr.toStrTransform()}` );
    }
  }

  public activeTransformCount(): number { return this.cur_transforms.length; }

  public stopAllTransforms(): void
  {
    for ( let i = this.cur_transforms.length - 1; i >= 0; i-- )
    {
      const tr = this.cur_transforms[i];
      tr.callOnFinished();
      this.cur_transforms.splice( i, 1 );
      this.onTransformRemoved( tr, false );
    }

    this.is_animating = false;
    this.has_important_animation = false;
    this.dom_opacity_driven = false;
    this.dom_scale_driven = false;
  }

  protected onTransformAdded( _transform: TransformBase ): void {}
  protected onTransformRemoved( _transform: TransformBase, _finished: boolean ): void {}
  protected onTransformRescheduled( _transform: TransformBase ): void {}

  protected detachDomTransforms(): void
  {
    for ( const tr of this.cur_transforms )
      this.onTransformRemoved( tr, false );
  }

  public domImportantPredecessor( transform: TransformBase ): TransformBase | null
  {
    let latest: TransformBase | null = null;
    for ( const tr of this.cur_transforms )
    {
      if ( tr === transform || !tr.isImportant() || tr.dom_animation === null )
        continue;
      if ( tr.getType() !== transform.getType() || tr.startTime() > transform.startTime() )
        continue;
      if ( !latest || tr.startTime() > latest.startTime() )
        latest = tr;
    }
    return latest;
  }

  public addTransform( transform:TransformBase )
  {
    if ( transform.isDecorative() && !uiEffectsEnabled() )
      return;

    // only one 'important' transformation of the same type should be active at a time
    if ( transform.isImportant() )
    {
      //const now = performance.now();
      //console.log( `addTransform()   now ${now}  start_time ${transform.startTime()}   isStarted ${transform.isStarted( now )}` );

      //if ( transform.isStarted( now ) )
      this.has_important_animation = true;

      for ( let i = this.cur_transforms.length - 1; i >= 0; --i )
      {
        const tr:TransformBase = this.cur_transforms[i];
        if ( !tr.isImportant() )
          continue;

        if ( tr.getType() != transform.getType() )
          continue;

        //console.log( 'an important transform of the same type is found' );

        if ( tr.isInsideIn( transform ) )
        {
          //console.log( 'isInsideIn() is true' );
          this.cur_transforms.splice( i, 1 );
          this.onTransformRemoved( tr, false );
          continue;
        }

        if ( tr.isBefore_andIntersects( transform ) )
        {
          //console.log( 'isBefore_andIntersects() is true' );
          tr.cutOffTheEndingBy( transform );
          //tr.setDestination( transform.startValue() );
          continue;
        }

        if ( tr.isAfter_andIntersects( transform ) )
        {
          //console.log( 'isAfter_andIntersects() is true' );
          tr.cutOffTheBeginningBy( transform );
          this.onTransformRescheduled( tr );
          continue;
        }

        //console.log( 'does not intersect' );
        // //tr.clipDurationBy( transform );
      }
    }

    this.cur_transforms.push( transform );
    this.onTransformAdded( transform );
  }

  public fadeOut( duration: number, start_time?: number, easing: Easing = Easing.Linear ): void
  {
    const anim_start_time = start_time ?? performance.now();
    const fadeTransform = new TransformOpacity(
      this,
      true,
      null,
      0.0,
      anim_start_time,
      duration,
      { easing: easing }
    );
    this.addTransform( fadeTransform );
  }

  public fadeIn( duration: number, start_time?: number, easing: Easing = Easing.Linear ): void
  {
    const anim_start_time = start_time ?? performance.now();
    const fadeTransform = new TransformOpacity(
      this,
      true,
      null,
      1.0,
      anim_start_time,
      duration,
      { easing: easing }
    );
    this.addTransform( fadeTransform );
  }

  public fadeToOpacity( target_opacity: number, duration: number, start_time?: number, easing: Easing = Easing.Linear ): void
  {
    const anim_start_time = start_time ?? performance.now();
    const fadeTransform = new TransformOpacity(
      this,
      true,
      null,
      target_opacity,
      anim_start_time,
      duration,
      { easing: easing }
    );
    this.addTransform( fadeTransform );
  }

  public fadeOutAndIn( fade_out_duration: number, fade_in_duration: number, delay_between: number = 0, start_time?: number ): void
  {
    const anim_start_time = start_time ?? performance.now();
    this.fadeOut( fade_out_duration, anim_start_time );
    const fade_in_start = anim_start_time + fade_out_duration + delay_between;
    this.fadeIn( fade_in_duration, fade_in_start );
  }

  /*
    public resetAllTransformations()
    {
      this.cur_transforms.length = 0; // maybeTODO: only important?
    }
  */
  public removeAllUnimportantScales()
  {
    for ( let i = this.cur_transforms.length - 1; i >= 0; --i )
    {
      const tr = this.cur_transforms[i];
      if ( tr.isImportant() )
        continue;

      if ( tr.getType() != TransformType.Scaling )
        continue;

      this.cur_transforms.splice( i, 1 );
      this.onTransformRemoved( tr, false );
    }
  }

  protected updateAllTransforms( now:number )
  {
    //console.log( `updateAllTransforms( now ${now} )  this.cur_transforms.length ${this.cur_transforms.length}` );
    this.is_animating = false;
    this.has_important_animation = false;
    this.dom_opacity_driven = false;
    this.dom_scale_driven = false;

    if ( this.cur_transforms.length === 0 )
      return;

    this.pixel_pos_addon.set( 0, 0 );
    this.scale_addon    .set( 1, 1 );
    this.opacity_addon = 1.0;

    this.rotation_addon = 0;

    //for ( let i = this.cur_transforms.length - 1; i >= 0; --i )
    for ( let i = 0; i < this.cur_transforms.length; ++i )
    {
      const transform = this.cur_transforms[i];
      if ( !transform.isStarted( now ) )
      {
        if ( transform.isImportant() )
          this.has_important_animation = true;
        continue;
      }

      if ( transform.dom_animation !== null && !transform.isDomPhaseOver( now ) )
      {
        this.is_animating = true;
        if ( transform.isImportant() )
          this.has_important_animation = true;
        if ( transform.getType() === TransformType.Opacity )
          this.dom_opacity_driven = true;
        if ( transform.getType() === TransformType.Scaling )
          this.dom_scale_driven = true;
        continue;
      }

      this.should_be_rendered = true;

      transform.updateTransform( now );
      if ( transform.isFinished( now ) )
        continue;

      this.is_animating = true;

      if ( transform.isImportant() )
        this.has_important_animation = true;
    }

    this.pixel_pos_for_render.copyFrom( this.pixel_pos );
    this.scale_for_render    .copyFrom( this.scale     );
    this.opacity_for_render = this.opacity;

    this.pixel_pos_for_render.addThis( this.pixel_pos_addon );
    this.scale_for_render.multiplyThis_elementByElement( this.scale_addon );
    this.opacity_for_render *= this.opacity_addon;

    this.rotation_for_render = this.rotation + this.rotation_addon;

    this.opacity_for_render = Math.max(0.0, Math.min(1.0, this.opacity_for_render));

    //console.log( `   pixel_pos ${this.pixel_pos}   pixel_pos_for_render ${this.pixel_pos_for_render}   scale ${this.scale}   scale_for_render ${this.scale_for_render}   opacity_for_render ${this.opacity_for_render}` );
  }

  public clearFinishedTransforms_andCallOnFinished( now:number )
  {
    //console.log( `clearFinishedTransforms_andCallOnFinished()  this.cur_transforms.length ${this.cur_transforms.length}` );
    if ( this.cur_transforms.length === 0 )
      return;

    for ( let i = this.cur_transforms.length - 1; i >= 0; --i )
    {
      const transform = this.cur_transforms[i];
      if ( !transform.isFinished( now ) )
        continue;

      // the animation is finished, remove it
      transform.callOnFinished();
      this.cur_transforms.splice( i, 1 );
      this.onTransformRemoved( transform, true );
    }
  }

  public    pixelPosForRender(): Vector2 { return this.pixel_pos_for_render; }
  public    pixelPosImportant(): Vector2 { return this.pixel_pos; }

  protected getScaleForRender(): Vector2 { return this.scale_for_render; }
  public    getScaleImportant(): Vector2 { return this.scale; }
  public    getScale(): Vector2 { return this.scale_for_render; }

  protected getOpacityForRender(): number { return this.opacity_for_render; }
  public    getOpacityImportant(): number { return this.opacity; }
  public    getOpacity(): number { return this.opacity_for_render; }

  //public getScaleX(): number { return this.scale.x; }
  //public getScaleY(): number { return this.scale.y; }

  public recalcPixelPosForRender()
  {
    this.pixel_pos_for_render.copyFrom( this.pixel_pos );
    this.pixel_pos_for_render.addThis( this.pixel_pos_addon );
  }

  public setScaleImmediate( scale: Vector2 ): void
  {
    this.scale.copyFrom( scale );
    this.scale_for_render.copyFrom( scale );
    this.markSet();
  }

  public setOpacityImmediate( opacity: number ): void
  {
    this.opacity = Math.max( 0, Math.min( 1, opacity ) );
    this.opacity_for_render = this.opacity;
    this.markSet();
  }

  public setPixelPos( p:Vector2, update_pixel_pos_for_render:boolean ): void
  {
    //if ( p.x == 402 )
    //  console.log( `setPixelPos( p.y ${fmt2(p.y)} )` );

    this.pixel_pos.copyFrom( p );

    if ( update_pixel_pos_for_render )
      this.recalcPixelPosForRender();
    this.markSet();
  }
  public setScale( scale:Vector2 ):void
  {
    this.scale.copyFrom( scale );
    this.markSet();
  }

  public setOpacity( opacity: number ): void
  {
    this.opacity = Math.max(0, Math.min(1, opacity));
    this.markSet();
  }

  public addToPixelPos( p:Vector2 )
  {
    this.pixel_pos_addon.addThis( p );
    this.markSet();
  }
  public addToScale( s:Vector2 )
  {
    this.scale_addon.multiplyThis_elementByElement( s );
    this.markSet();
  }

  public addToOpacity( opacity: number )
  {
    this.opacity_addon *= opacity;
    this.markSet();
  }

  public isAnimating(): boolean
  {
    //return (this.cur_transforms.length > 0)
    //    || (this.non_important_loop_transform !== null);
    return this.is_animating;
  }

  public hasImportangAnimation(): boolean
  {
/*
    if ( this.cur_transforms.length === 0 )
      return false;

    if ( this.cur_transforms.length > 1 )
      return true;

    const trq:TransformsQueue = this.cur_transforms[0];
    trq.transforms.forEach( tr => {
      if ( tr.isImportantForMoving() )
        return true;
    } );

    return false;
*/
    return this.has_important_animation;
  }

  public shouldBeRendered(): boolean
  {
    return this.should_be_rendered && ( this.opacity_for_render > 0.001 || this.dom_opacity_driven );
  }

  public isDomOpacityDriven(): boolean
  {
    return this.dom_opacity_driven;
  }

  public isDomScaleDriven(): boolean
  {
    return this.dom_scale_driven;
  }

  public getRotationImportant(): number { return this.rotation; }
  public getRotationForRender(): number { return this.rotation_for_render; }

  public setRotation( angle: number ): void
  {
    this.rotation = angle;
    this.markSet();
  }

  public setRotationImmediate( angle: number ): void
  {
    this.rotation = angle;
    this.rotation_for_render = angle;
    this.markSet();
  }

  public addToRotation( delta: number ): void
  {
    this.rotation_addon += delta;
    this.markSet();
  }

  public setRotationDeg( deg: number ): void 
  {
    this.setRotation( deg * Math.PI / 180 );
  }

  public addToRotationDeg(deltaDeg: number): void  
  {
    this.addToRotation(deltaDeg * Math.PI / 180);
  }
  
  protected setShouldBeRendered(): void
  {
    this.should_be_rendered = true;
    this.explicit_dirty = true;
  }

  protected markDirtyFromChild(): void
  {
    this.should_be_rendered = true;
  }

  public isExplicitlyDirty(): boolean
  {
    return this.explicit_dirty;
  }

  protected resetShouldBeRendered(): void
  {
    this.should_be_rendered = false;
    this.explicit_dirty = false;
  }

  private markSet(): void
  {
    if ( this.dirty_on_set )
      this.should_be_rendered = true;
  }

  
}
